import React, { ReactNode, useCallback, useMemo } from 'react'
import { GridRowMenuItemsProps, GridRowMenuOffset } from 'core/elements/grid/hooks/useGridRowMenu'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import useToggler from 'core/hooks/useToggler'
import { middleLeft } from 'core/elements/menu/defaults'
import PortalMenu from 'core/elements/menu/PortalMenu'
import MenuItem from 'core/elements/menu/MenuItem'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import GridRowMenuItemsSection from './GridRowMenuItemsSection'
import Tooltip from 'core/elements/tooltip'

interface GridRowMenuProps<T> extends GridRowMenuItemsProps<T> {
  item: T
  rowMenuOffset?: GridRowMenuOffset
  // Default behavior is that when there is only one row action for an item,
  // display just a button instead of a menu/dropdown. Set this to false if you
  // still want the menu to show instead of a single action button
  showRowMenuForSingleRowActions?: boolean
  maxRowMenuHeight?: number
  toggleRow?: () => void
}

export default function GridRowMenu<T>({
  item,
  rowMenuItems,
  rowMenuDisabled,
  rowMenuOffset = {},
  showRowMenuForSingleRowActions = false,
  maxRowMenuHeight,
  toggleRow,
}: GridRowMenuProps<T>) {
  const filteredRowMenuItems = rowMenuItems.filter((rowItem) => {
    if (rowItem?.hideIfDisabled) {
      return !rowItem?.getIsDisabled(item)
    }
    return true
  })

  const classes = useStyles()
  const [isOpen, toggleIsOpen] = useToggler()
  const handleMenuClick = useCallback((e) => {
    e.stopPropagation()
    toggleIsOpen()
    // Workaround for clicking anchor causing row to be toggled
    // Retoggle the row back if clicking on anchor
    // toggleRow()
  }, [])

  if (rowMenuDisabled || !filteredRowMenuItems.length) {
    return null
  }

  if (!showRowMenuForSingleRowActions && filteredRowMenuItems.length === 1) {
    const [{ RowMenuButton, icon, label, getIsDisabled, triggerAction }] = filteredRowMenuItems
    return (
      <div className={clsx('rowMenu', classes.gridRowMenu)}>
        {label ? (
          <RowMenuButton
            disabled={getIsDisabled(item)}
            icon={icon}
            onClick={() => triggerAction(item)}
          >
            {label}
          </RowMenuButton>
        ) : (
          <FontAwesomeIcon disabled={getIsDisabled(item)} onClick={() => triggerAction(item)}>
            {icon}
          </FontAwesomeIcon>
        )}
      </div>
    )
  }

  return (
    <PortalMenu
      align={middleLeft.align}
      offset={{
        ...middleLeft.offset,
        ...rowMenuOffset,
      }}
      origin="top left"
      onClick={handleMenuClick}
      className={clsx('rowMenu', classes.gridRowMenu)}
      style={
        {
          ['--max-row-menu-height' as any]: maxRowMenuHeight,
        } as React.CSSProperties
      }
      anchor={<FontAwesomeIcon>ellipsis-vertical</FontAwesomeIcon>}
      open={isOpen}
      onClose={toggleIsOpen}
    >
      {filteredRowMenuItems.map(
        ({ key, icon, label, getIsDisabled, triggerAction, items, tooltip }) =>
          items ? (
            <GridRowMenuItemsSection key={key} items={items} label={label} item={item} />
          ) : (
            <MenuItem
              key={key}
              readonly={getIsDisabled(item)}
              icon={icon}
              onClick={() => triggerAction(item)}
            >
              <Tooltip
                className={classes.tooltip}
                message={
                  (typeof tooltip === 'function' ? tooltip(item) : tooltip) as string | ReactNode
                }
              >
                {label}
              </Tooltip>
            </MenuItem>
          ),
      )}
    </PortalMenu>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  gridRowMenu: {
    cursor: 'pointer',
    '& > i': {
      fontWeight: 'bold',
    },
    textAlign: 'center',
    lineHeight: '56px',
    // visibility: 'hidden',
    position: 'absolute',
    marginTop: 1,
    inset: '0 0 0 auto',
    padding: theme.spacing(0, 1),
    minWidth: 20,
    backgroundColor: 'inherit',
    transition: 'visibility 150ms ease',
    '& .menu-popover': {
      minWidth: 60,
      padding: '8px',
    },
  },
  warning: {
    color: theme.palette.red[500],
  },
}))
